import captureException from 'lib/captureException';
import createDealerHours, { DealerHours } from 'lib/models/DealerHours';
import { DealerReview } from 'lib/models/DealerReview';

interface DealerProps {
  displayName: string;
  remoteId?: string;
  providerName?: string;
  dealerName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phoneRegional: string;
  website: string;
  dealerHours: Record<string, string>;
  nearbyDealers: NearbyDealer[];
}

export interface DealerAddress {
  isValid: boolean;
  fullAddress: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface NearbyDealer {
  name: string;
  dealerPageUrl: string;
}

export type Dealer = {
  name: string;
  displayName: string;
  recommendedPercentage: number | null;
  remoteId?: string;
  providerName?: string;
  averageOverallStars: number | null;
  averagePricingStars: number | null;
  averageServiceStars: number | null;
  averageHelpfulnessStars: number | null;
  averageResponsivenessStars: number | null;
  totalReviews: number;
  hours: DealerHours | undefined;
  phoneRegional: string;
  website: string;
  websiteName: string;
  address: DealerAddress;
  nearbyDealers: NearbyDealer[];
};

function getWebsiteName(site: string): string {
  let siteName = site;
  try {
    const url = new URL(site);
    siteName = url.hostname;
  } catch (e) {
    captureException(e);
  }
  return siteName;
}

function calculateRecommendedPercentage(reviews: DealerReview[]): number | null {
  if (!reviews || (reviews && reviews.length === 0)) return null;

  const numberOfRecs = reviews.filter((review) => review.recommended).length;

  return Math.round((numberOfRecs / reviews.length) * 100);
}

function calculateAverageRating(ratings: number[]): number | null {
  if (ratings.length === 0) return null;

  const sum = ratings.reduce((acc, review) => acc + review, 0);
  return sum / ratings.length;
}

function calculateStarsByType(reviews: DealerReview[], type: keyof DealerReview): number | null {
  const ratings = reviews
    .map((review) => review[type])
    .filter((rating): rating is number => !!rating);
  return calculateAverageRating(ratings);
}

export function createDealer(props: DealerProps, reviews: DealerReview[]): Dealer {
  const { street, city, state, zip } = props;
  const { dealerName, displayName, remoteId, providerName, website, nearbyDealers, dealerHours } =
    props;
  const isValid = !!(street && city && state && zip);
  const fullAddress = [street, city, state, zip].join(', ');
  const isDealerHoursEmpty = Object.keys(dealerHours).length === 0;

  return {
    ...props,
    name: dealerName,
    displayName,
    recommendedPercentage: calculateRecommendedPercentage(reviews),
    remoteId: remoteId?.toLowerCase(),
    providerName: providerName?.toLowerCase(),
    averageOverallStars: calculateStarsByType(reviews, 'overallRating'),
    averagePricingStars: calculateStarsByType(reviews, 'priceRating'),
    averageServiceStars: calculateStarsByType(reviews, 'serviceRating'),
    averageHelpfulnessStars: calculateStarsByType(reviews, 'helpRating'),
    averageResponsivenessStars: calculateStarsByType(reviews, 'processRating'),
    totalReviews: reviews.length,
    hours:
      dealerHours || !isDealerHoursEmpty
        ? createDealerHours({ hoursData: dealerHours })
        : undefined,
    website,
    websiteName: getWebsiteName(website),
    address: {
      isValid,
      fullAddress,
      street,
      city,
      state,
      zip,
    },
    nearbyDealers,
  };
}
